import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import * as photoService from '@/api/services/photo.service';
import type {
  PhotoBulkActionPayload,
  PhotoFilters,
  PhotoUpdatePayload,
} from '@/api/types/photo.types';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const PHOTO_KEYS = {
  all: ['photos'] as const,
  lists: () => [...PHOTO_KEYS.all, 'list'] as const,
  list: (filters?: PhotoFilters) => [...PHOTO_KEYS.lists(), filters] as const,
  details: () => [...PHOTO_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...PHOTO_KEYS.details(), id] as const,
};

// ---------------------------------------------------------------------------
// usePhotos – infinite query with cursor pagination
// ---------------------------------------------------------------------------

/**
 * Fetches photos with cursor-based infinite scrolling.
 * Each page adds to the accumulated results so the virtualised grid
 * can render them as a single flat list.
 */
export function usePhotos(filters?: PhotoFilters) {
  return useInfiniteQuery({
    queryKey: PHOTO_KEYS.list(filters),
    queryFn: ({ pageParam }) =>
      photoService.getPhotos({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      // The cursor is embedded in the `next` URL as a query param.
      try {
        const url = new URL(lastPage.next, window.location.origin);
        return url.searchParams.get('cursor') ?? undefined;
      } catch {
        return undefined;
      }
    },
  });
}

// ---------------------------------------------------------------------------
// usePhoto – single photo detail
// ---------------------------------------------------------------------------

export function usePhoto(photoId: string) {
  return useQuery({
    queryKey: PHOTO_KEYS.detail(photoId),
    queryFn: () => photoService.getPhoto(photoId),
    enabled: !!photoId,
  });
}

// ---------------------------------------------------------------------------
// useUpdatePhoto
// ---------------------------------------------------------------------------

export function useUpdatePhoto() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      photoId,
      payload,
    }: {
      photoId: string;
      payload: PhotoUpdatePayload;
    }) => photoService.updatePhoto(photoId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: PHOTO_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: PHOTO_KEYS.detail(variables.photoId),
      });
    },
    onError: () => {
      toast.error(t('gallery.updateError'));
    },
  });
}

// ---------------------------------------------------------------------------
// useBulkAction
// ---------------------------------------------------------------------------

export function useBulkAction() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: PhotoBulkActionPayload) =>
      photoService.bulkAction(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: PHOTO_KEYS.lists() });
      toast.success(
        t('gallery.bulkSuccess', {
          count: data.affected,
          action: data.action,
        }),
      );
    },
    onError: () => {
      toast.error(t('gallery.bulkError'));
    },
  });
}

// ---------------------------------------------------------------------------
// useDeletePhoto
// ---------------------------------------------------------------------------

export function useDeletePhoto() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (photoId: string) => photoService.deletePhoto(photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHOTO_KEYS.lists() });
      toast.success(t('gallery.deleteSuccess'));
    },
    onError: () => {
      toast.error(t('gallery.deleteError'));
    },
  });
}
